import { useContext, useMemo } from 'react';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { TrailCamHistorySectionRow } from 'app/agriculture/routes/Devices/DeviceDetails/components/display/trailcam/TrailCamHistorySectionRow';
import { useSnapshotQueryData } from 'app/agriculture/queries/snapshots/useSnapshotQueryData';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { TZDate } from '@date-fns/tz';
import { useTimelineInterval } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { filterSnapshots } from 'app/agriculture/queries/snapshots/utils/filterSnapshots';
import { intervalToDuration } from 'date-fns';
import { ContentLayout } from 'app/agriculture/components/ContentLayout/ContentLayout';
import { useCursorTimeForSnapshotType } from 'app/agriculture/queries/snapshots/useCursorTimeForSnapshotType';

export function TrailCamHistorySection() {
    const { device } = useContext(DeviceDetailContext);
    const { timeZone } = useTimeZone();
    const timelineInterval = useTimelineInterval();

    const shelfLife = useMemo(() => intervalToDuration(timelineInterval), [timelineInterval]);

    // get all snapshots for the timeline interval
    const snapshotData = useSnapshotQueryData({
        snapshotType: 'Image',
        cursorTime: timelineInterval.end,
        shelfLife,
    });

    // need to timezoneify the timestamps
    const filteredSnapshots = useMemo(
        () =>
            filterSnapshots(snapshotData.get(device.id) ?? [], {
                cursorTime: timelineInterval.end,
                shelfLife,
            }).map((snapshot) => ({
                ...snapshot,
                timestamp: new TZDate(snapshot.timestamp, timeZone),
            })),
        [snapshotData, device.id, timelineInterval.end, shelfLife, timeZone],
    );

    const cursorTime = useCursorTimeForSnapshotType({ snapshotType: 'Image' });
    const [recentSnapshot] = useMemo(
        () => filterSnapshots(snapshotData.get(device.id) ?? [], { cursorTime }),
        [snapshotData, device.id, cursorTime],
    );

    // if there are no snapshots
    if (filteredSnapshots.length === 0) {
        return null;
    }

    return (
        <ContentLayout includeSeparator>
            {filteredSnapshots.map((snapshot) => (
                <TrailCamHistorySectionRow
                    key={snapshot.id}
                    snapshot={snapshot}
                    isCurrent={snapshot.id === recentSnapshot?.id}
                />
            ))}
        </ContentLayout>
    );
}
